import React, { Component } from 'react';
import ComponentName from '../../../constant/ComponentName';
import ItemToolsMenu from '../../ItemToolsMenu';
import { query } from '@trlogic/client-js';
import _ from 'lodash';
import {
  CustomFilter as FilterType,
  CustomFilterCategorical,
  CustomFilterDateSearch,
  CustomFilterFields,
  CustomFilterLiteralSearch,
} from '../../../domain/ui/FilterDomains';
import FilterUtil from '../../../util/FilterUtil';
import { dispatch, RootState } from '../../../redux/store';
import { changePageFilter } from '../../../redux/actions/PageActions';
import ValueFactory from '../../../util/ValueFactory';
import ServiceContainer from '../../../config/ServiceContainer';
import { ServiceNames } from '../../../constant/ServiceNames';
import CustomTable, { CustomColumnProps } from '../../custom/CustomTable';
import CustomTableContentWrapper from '../../custom/CustomTableContentWrapper';
import moment from 'moment/moment';
import CustomPaginator, { CustomPaginatorProps } from '../../custom/CustomPaginator';
import CustomLoading from '../../custom/CustomLoading';
import AdminAuditDetailComponent from './AdminAuditDetailComponent';
import { AdminAuditsExportJob, AuditLogExportJob } from '../../../domain/job/JobDomain';
import MessageUtil from '../../../util/MessageUtil';
import JobService from '../../../service/JobService';
import { DataTableSortMeta } from 'primereact/datatable';
import DataType from '../../../constant/DataType';
import { PageState } from '../../../redux/reducers/PageReducer';
import { connect } from 'react-redux';
import { changeCaseColumns, changeListDataLoading } from '../../../redux/actions/CaseActions';
import { setAttribute } from '../../../redux/actions/UserActions';
import { ConnectedProps } from 'react-redux/es/exports';
import NavigationUtil from '../../../util/NavigationUtil';
import { AuditClient } from '../../../client/audit/AuditClient';
import GatewayContainer from '../../../config/GatewayContainer';
import GatewayNames from '../../../constant/GatewayNames';
import { AuditLog, AuditLogType } from '../../../domain/rbac/RbacDomains';
import Filter = query.Filter;
import empty = query.empty;
import Order = query.Order;
import or = query.or;
import eq = query.eq;
import and = query.and;
import QueryBuilder = query.QueryBuilder;
import neq = query.neq;
import LanguageUtil from '../../../util/LanguageUtil';

class AdminAuditsComponent extends Component<Props, State> {
  public state = new State();

  private auditClient: AuditClient = GatewayContainer.getInstance().get(GatewayNames.AUDIT_CLIENT);
  private readonly jobService: JobService = ServiceContainer.getInstance().get(ServiceNames.JOB);

  public render() {
    const { filter, auditLogs, rows, currentPage, loading, totalRecord } = this.state;
    return (
      <div className={'flex flex-col'}>
        <ItemToolsMenu
          onChangeFilter={this.onChangeFilter}
          filter={filter}
          exportVisible
          exportVisibleAlways
          exportFunc={() => this.onExport()}
          defaultFilterDisabled={true}
          importVisible={false}
          component={ComponentName.ADMIN_AUDIT_LOG}
          componentLabel={LanguageUtil.get({ id: 'pages.admin.audit.admin.title', defaultMessage: 'Admin Audit Logs' })}
        />
        <div className={'flex w-full h-full max-h-[calc(100dvh-360px)] overflow-scroll'}>
          {loading ? (
            <CustomLoading />
          ) : (
            <CustomTable onSortChange={this.onSortChange} multiSortMeta={this.getOrder()} columns={this.getColumns()} value={auditLogs} />
          )}
          <CustomPaginator
            rows={rows}
            totalRecords={totalRecord}
            rowsPerPageOptions={[5, 10, 20, 50]}
            onPageChange={this.onPageChange}
            currentPage={currentPage}
          />
        </div>
      </div>
    );
  }

  public async componentDidMount() {
    this.prepareFilterFields();
    const count = await this.getCount();
    this.setState({ totalRecord: count }, this.getAudits);
  }

  private prepareFilterFields = async () => {
    const fields: CustomFilterFields[] = await FilterUtil.getFields(ComponentName.ADMIN_AUDIT_LOG);
    dispatch(
      changePageFilter({
        filter: ValueFactory.initializeFilter(),
        items: fields,
      }),
    );
  };

  private onChangeFilter = async (filter: Filter) => {
    const isSame = _.isEqual(filter, this.state.filter);
    if (isSame) return;
    this.setState({ filter, currentPage: 1, totalRecord: await this.getCount() }, this.getAudits);
  };

  private getCount = (): Promise<number> => {
    const endpoint = `audit_log_ui_${NavigationUtil.getTenant()}_*`;
    const _query = query.QueryBuilder.select()
      .from(endpoint)
      .where(
        and(
          this.state.filter,
          or(
            eq('component.keyword', ComponentName.ADMIN_USER),
            eq('component.keyword', ComponentName.ADMIN_ROLE),
            eq('component.keyword', ComponentName.ADMIN_GROUP),
          ),
          or(eq('operation.keyword', 'CREATE'), eq('operation.keyword', 'UPDATE'), eq('operation.keyword', 'DELETE')),
          neq('username.keyword', 'SYSTEM'),
        ),
      )
      .build();
    return this.auditClient.getCount(_query);
  };

  private getAudits = async () => {
    const { currentPage, rows, sorts } = this.state;
    this.setState({ loading: true });
    const endpoint = `audit_log_ui_${NavigationUtil.getTenant()}_*`;
    const filter = and(
      this.state.filter,
      or(
        eq('component.keyword', ComponentName.ADMIN_USER),
        eq('component.keyword', ComponentName.ADMIN_ROLE),
        eq('component.keyword', ComponentName.ADMIN_GROUP),
      ),
      or(eq('operation.keyword', 'CREATE'), eq('operation.keyword', 'UPDATE'), eq('operation.keyword', 'DELETE')),
      neq('username.keyword', 'SYSTEM'),
    );
    const audits: AuditLog[] = await this.auditClient.getComponentAudits((currentPage - 1) * rows, rows, endpoint, filter, sorts);
    const count = await this.getCount();
    this.setState({ auditLogs: audits, loading: false, totalRecord: count });
  };

  public onPageChange = (event: CustomPaginatorProps) => {
    this.setState({ rows: event.rows, currentPage: event.page }, this.getAudits);
  };
  protected onSortChange = (sorts: Order[]) => {
    this.setState({ sorts }, this.getAudits);
  };

  private getOrder = (): DataTableSortMeta[] => {
    const { sorts } = this.state;
    return sorts.map((s) => {
      return {
        field: s.field,
        order: s.operator == 'ASC' ? 1 : -1,
      } as DataTableSortMeta;
    });
  };

  private addFilter = async (field: string, value: any, label: string, dataType: DataType, categorical: boolean = false) => {
    const { changePageFilter, page } = this.props;
    const filter: { filter: FilterType; items: CustomFilterFields[] } = page.filter;
    if (categorical) {
      filter.filter.fields.push({
        field: field,
        label: label,
        enabled: true,
        search: {
          type: 'CATEGORICAL',
          value: {
            values: [value],
          } as CustomFilterCategorical,
        },
      });
    } else {
      switch (dataType) {
        case DataType.STRING:
        case DataType.NUMBER:
          filter.filter.fields.push({
            field: field,
            label: label,
            enabled: true,
            search: {
              type: 'LITERAL',
              value: {
                properties: {
                  dataType: dataType,
                },
                operator: 'eq',
                value: value,
              } as CustomFilterLiteralSearch,
            },
          });
          break;
        case DataType.DATE:
          filter.filter.fields.push({
            field: field,
            label: label,
            enabled: true,
            search: {
              type: 'DATE',
              value: {
                startDate: {
                  label: moment(value).format('LLLL'),
                  value: value,
                  type: 'Absolute',
                },
                endDate: {
                  label: moment(value).format('LLLL'),
                  value: value,
                  type: 'Absolute',
                },
              } as CustomFilterDateSearch,
            },
          });
          break;
        case DataType.BOOLEAN:
          await this.addFilter(field, value, label, dataType, true);
          break;
        default:
          return;
      }
    }
    changePageFilter(filter);
    this.onChangeFilter(this.parseFilter());
  };

  private parseFilter = () => {
    const { page } = this.props;
    const {
      filter: { filter },
    }: { filter: { filter: FilterType } } = page;
    return FilterUtil.parse(filter);
  };

  private getColumns = (): CustomColumnProps[] => {
    return [
      {
        field: 'username.keyword',
        header: LanguageUtil.get({ id: 'common.label.username', defaultMessage: 'Username' }),
        className: 'w-[10%]',
        filterable: true,
        copyable: true,
        onCopy: (e: AuditLog) => {
          return e.username;
        },
        addFilter: (e: AuditLog) => {
          this.addFilter('username.keyword', e.username, 'Username', DataType.STRING, true);
        },
        body: (record: AuditLog) => {
          return (
            <div className={'text-xs 2xl:text-md'}>
              <CustomTableContentWrapper
                clipSize={25}
                showEllipsis={true}
                text={record.username} />
            </div>
          );
        },
      },
      {
        field: 'operation.keyword',
        header: LanguageUtil.get({ id: 'common.label.action', defaultMessage: 'Action' }),
        className: 'w-[10%]',
        filterable: true,
        copyable: true,
        onCopy: (e: AuditLog) => {
          return e.operation;
        },
        addFilter: (e: AuditLog) => {
          this.addFilter('operation.keyword', e.operation, 'Action', DataType.STRING, true);
        },
        body: (record: AuditLog) => {
          let operationType = record.operation;
          return (
            <div className={'text-xs 2xl:text-md'}>
              <CustomTableContentWrapper text={operationType} />
            </div>
          );
        },
      },
      {
        field: 'component.keyword',
        header: LanguageUtil.get({ id: 'common.label.type', defaultMessage: 'Type' }),
        className: 'w-[10%]',
        filterable: true,
        copyable: true,
        onCopy: (e: AuditLog) => {
          return this.handleType(e.component);
        },
        addFilter: (e: AuditLog) => {
          this.addFilter('component.keyword', e.component, 'Type', DataType.STRING, true);
        },
        body: (record: AuditLog) => {
          const type = this.handleType(record.component);
          return (
            <div className={'text-xs 2xl:text-md'}>
              <CustomTableContentWrapper text={type ?? ''} />
            </div>
          );
        },
      },
      {
        field: 'document.id.keyword',
        header: LanguageUtil.get({ id: 'common.label.resourceId', defaultMessage: 'Resource Id' }),
        className: 'w-[15%]',
        filterable: true,
        copyable: true,
        onCopy: (e: AuditLog) => {
          return e.document.id;
        },
        addFilter: (e: AuditLog) => {
          this.addFilter('document.id.keyword', e.document.id, 'Resource Id', DataType.STRING);
        },
        body: (record: AuditLog) => {
          return (
            <div className={'text-xs 2xl:text-md'}>
              <CustomTableContentWrapper clipSize={20} showEllipsis={true} text={record.document.id} />
            </div>
          );
        },
      },
      {
        field: 'protocol',
        header: LanguageUtil.get({ id: 'common.label.protocol', defaultMessage: 'Protocol' }),
        className: 'w-[6%] min-w-[90px]',
        filterable: true,
        copyable: true,
        onCopy: (e: AuditLog) => e.protocol ?? '',
        addFilter: (e: AuditLog) => {
          if (e.protocol)
            this.addFilter('protocol', e.protocol, LanguageUtil.get({ id: 'common.label.protocol', defaultMessage: 'Protocol' }), DataType.STRING, true);
        },
        body: (record: AuditLog) => (
          <div className={'text-xs 2xl:text-md'}>
            <CustomTableContentWrapper text={record.protocol ?? '-'} />
          </div>
        ),
      },
      {
        field: 'port',
        header: LanguageUtil.get({ id: 'common.label.port', defaultMessage: 'Port' }),
        className: 'w-[5%] min-w-[70px]',
        filterable: true,
        copyable: true,
        onCopy: (e: AuditLog) => e.port?.toString() ?? '',
        addFilter: (e: AuditLog) => {
          if (e.port != undefined) this.addFilter('port', e.port, LanguageUtil.get({ id: 'common.label.port', defaultMessage: 'Port' }), DataType.NUMBER);
        },
        body: (record: AuditLog) => (
          <div className={'text-xs 2xl:text-md'}>
            <CustomTableContentWrapper text={record.port?.toString() ?? '-'} />
          </div>
        ),
      },
      {
        field: 'timestamp',
        header: LanguageUtil.get({ id: 'common.label.date', defaultMessage: 'Date' }),
        className: 'w-[20%] min-w-[260px]',
        filterable: true,
        copyable: true,
        onCopy: (e: AuditLog) => {
          return moment(e.timestamp).format('LLLL').valueOf();
        },
        addFilter: (e: AuditLog) => {
          this.addFilter('timestamp', e.timestamp, 'Time', DataType.DATE);
        },
        body: (record: AuditLog) => {
          return (
            <div className={'text-xs 2xl:text-md'}>
              <CustomTableContentWrapper text={moment(record.timestamp).format('LLLL').valueOf()} />
            </div>
          );
        },
      },
      {
        field: 'detail',
        header: LanguageUtil.get({ id: 'common.label.details', defaultMessage: 'Details' }),
        className: 'min-w-[120px]',
        body: (record: AuditLog) => {
          return <AdminAuditDetailComponent audit={record} />;
        },
      },
    ];
  };

  private async onExport() {
    const endpoint = `audit_log_ui_${NavigationUtil.getTenant()}_*`;
    const query = QueryBuilder.select()
      .from(endpoint)
      .where(
        and(
          this.state.filter,
          or(
            eq('component.keyword', ComponentName.ADMIN_USER),
            eq('component.keyword', ComponentName.ADMIN_ROLE),
            eq('component.keyword', ComponentName.ADMIN_GROUP),
          ),
          or(eq('operation.keyword', 'CREATE'), eq('operation.keyword', 'UPDATE'), eq('operation.keyword', 'DELETE')),
        ),
      )
      .build();
    const job: AuditLogExportJob = {
      type: 'AUDIT_LOG_EXPORT',
      timestamp: Date.now(),
      status: 'PENDING',
      query,
      representation: true,
      format: 'csv',
      extension: 'csv',
      path: '',
    };
    try {
      await this.jobService.schedule(job);
      MessageUtil.success({
        summary: 'Success',
        detail: 'Export job has been scheduled',
      });
    } catch (e) {}
  }

  private handleType(resourceType: ComponentName) {
    switch (resourceType) {
      case ComponentName.ADMIN_ROLE:
        return 'Role';
      case ComponentName.ADMIN_GROUP:
        return 'Group';
      case ComponentName.ADMIN_USER:
        return 'User';
      default:
        return resourceType;
    }
  }
}

class State {
  loading: boolean = true;
  filter: Filter = empty();
  auditLogs: AuditLog[] = [];
  rows: number = 10;
  currentPage: number = 1;
  totalRecord: number = 0;
  sorts: Order[] = [{ field: 'timestamp', operator: 'DESC' }];
}

interface Props extends ReduxProps {}

const mapStateToProps = (state: RootState) => ({
  page: state.page as PageState,
});

const connector = connect(mapStateToProps, { changeCaseColumns, setAttribute, changeListDataLoading, changePageFilter });
type ReduxProps = ConnectedProps<typeof connector>;
export default connector(AdminAuditsComponent);
